(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initReveal = (root) => {
    if (root.dataset.revealReady) return;
    root.dataset.revealReady = 'true';
    if ((window.Shopify && window.Shopify.designMode) || reducedMotion.matches) return;
    root.classList.add('eae--motion');
    if (!('IntersectionObserver' in window)) {
      root.querySelectorAll('[data-eae-reveal]').forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
    }), { threshold: 0.08, rootMargin: '0px 0px -8%' });
    root.querySelectorAll('[data-eae-reveal]').forEach((item) => observer.observe(item));
  };
  const initGallery = (root) => {
    if (root.dataset.galleryReady) return;
    root.dataset.galleryReady = 'true';
    const items = [...root.querySelectorAll('[data-item]')];
    const previous = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    if (items.length < 2 || !previous || !next) return;
    let active = 0;
    let timer;
    let pointerStart = null;
    const interval = Math.max(3000, Number(root.dataset.interval || 5000));
    const duration = Math.max(350, Number(root.dataset.duration || 700));
    const autoplay = root.dataset.autoplay === 'true' && !reducedMotion.matches;
    const focusPosition = Math.min(2, Math.floor((items.length - 1) / 2));
    const render = (animate = true) => {
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const canAnimate = animate && !reducedMotion.matches && !isMobile && 'animate' in Element.prototype;
      const previousRects = canAnimate ? new Map(items.map((item) => [item, {
        rect: item.getBoundingClientRect(),
        hidden: item.getAttribute('aria-hidden') === 'true',
        position: Number(item.dataset.position || 0)
      }])) : null;
      items.forEach((item, index) => {
        item.getAnimations().forEach((animation) => animation.cancel());
        const position = isMobile ? index : (index - active + focusPosition + items.length) % items.length;
        item.dataset.position = String(position);
        item.style.order = String(position);
        item.classList.toggle('is-active', isMobile ? index === active : position === focusPosition);
        item.setAttribute('aria-hidden', isMobile ? 'false' : (position > 4 ? 'true' : 'false'));
      });
      if (isMobile) {
        const track = root.querySelector('[data-track]');
        const current = items[active];
        if (track && current && animate) {
          const left = current.offsetLeft - (track.clientWidth - current.offsetWidth) / 2;
          track.scrollTo({ left: Math.max(0, left), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }
        return;
      }
      if (!canAnimate) return;
      items.forEach((item) => {
        const beforeState = previousRects.get(item);
        const before = beforeState.rect;
        const after = item.getBoundingClientRect();
        const isHidden = item.getAttribute('aria-hidden') === 'true';
        if (isHidden || beforeState.hidden || !before.width || !after.width) {
          if (!isHidden && beforeState.hidden) {
            item.animate([
              { opacity: 0 },
              { opacity: 1 }
            ], { duration: Math.min(duration, 500), easing: 'ease-out' });
          }
          return;
        }
        const deltaX = before.left - after.left;
        const deltaY = before.top - after.top;
        const afterPosition = Number(item.dataset.position || 0);
        const wrapsAcrossEdges = Math.abs(beforeState.position - afterPosition) > 2;
        if (wrapsAcrossEdges) {
          item.animate([
            { opacity: 0 },
            { opacity: 1 }
          ], { duration: Math.min(duration, 500), easing: 'ease-out' });
          return;
        }
        if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;
        item.animate([
          { transform: `translate3d(${deltaX}px, ${deltaY}px, 0)` },
          { transform: 'translate3d(0, 0, 0)' }
        ], { duration, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      });
    };
    const stop = () => window.clearInterval(timer);
    const start = () => { stop(); if (autoplay) timer = window.setInterval(() => go(1, false), interval); };
    const go = (direction, restart = true) => { active = (active + direction + items.length) % items.length; render(); if (restart) start(); };
    previous.addEventListener('click', (event) => { event.preventDefault(); go(-1); });
    next.addEventListener('click', (event) => { event.preventDefault(); go(1); });
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    root.addEventListener('pointerdown', (event) => { pointerStart = event.clientX; stop(); });
    root.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const delta = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(delta) > 45) go(delta < 0 ? 1 : -1); else start();
    });
    document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); else start(); });
    render(false); start();
  };
  const initJourney = (root) => {
    if (root.dataset.journeyReady) return;
    root.dataset.journeyReady = 'true';
    const panels = [...root.querySelectorAll('[data-panel]')];
    const imagesOne = [...root.querySelectorAll('[data-image-one]')];
    const imagesTwo = [...root.querySelectorAll('[data-image-two]')];
    const number = root.querySelector('[data-number]');
    const previous = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    if (!panels.length || !previous || !next) return;
    let active = 0;
    const show = (index) => {
      active = (index + panels.length) % panels.length;
      panels.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === active));
      imagesOne.forEach((item, itemIndex) => { item.hidden = itemIndex !== active; });
      imagesTwo.forEach((item, itemIndex) => { item.hidden = itemIndex !== active; });
      if (number) number.textContent = String(active + 1).padStart(2, '0');
    };
    previous.addEventListener('click', (event) => { event.preventDefault(); show(active - 1); });
    next.addEventListener('click', (event) => { event.preventDefault(); show(active + 1); });
    show(0);
  };
  const init = (scope = document) => {
    const pick = (selector) => scope.matches && scope.matches(selector) ? [scope] : [...scope.querySelectorAll(selector)];
    pick('.eae').forEach(initReveal);
    pick('[data-eae-gallery]').forEach(initGallery);
    pick('[data-eae-journey]').forEach(initJourney);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  if (!window.ElsvartEditorialEditorListener) {
    window.ElsvartEditorialEditorListener = true;
    document.addEventListener('shopify:section:load', (event) => init(event.target));
  }
})();

